import { useId, type JSX } from 'react';

type AwardBadgeType =
  | 'golden-kitty'
  | 'product-of-the-day'
  | 'product-of-the-month'
  | 'product-of-the-week';

interface AwardBadgeProps {
  type: AwardBadgeType;
  place?: number;
  label?: string;
  className?: string;
}

const BACKGROUND_BY_PLACE = ['#f3e3ac', '#ddd', '#f1cfa6'] as const;

const TITLE_BY_TYPE: Record<AwardBadgeType, string> = {
  'golden-kitty': 'Golden Kitty Awards',
  'product-of-the-day': 'Product of the Day',
  'product-of-the-month': 'Product of the Month',
  'product-of-the-week': 'Product of the Week',
};

const OVERLAY_COLORS = [
  'hsl(358, 100%, 62%)',
  'hsl(186, 95%, 55%)',
  'hsl(72, 85%, 65%)',
  'hsl(96, 100%, 50%)',
  'hsl(221, 85%, 55%)',
  'hsl(271, 85%, 47%)',
  'hsl(300, 20%, 35%)',
  'transparent',
  'transparent',
  'white',
] as const;

export function AwardBadge({ type, place, label, className }: AwardBadgeProps): JSX.Element {
  const reactId = useId();
  const uid = reactId.replace(/:/g, '');
  const blurId = `award-blur-${uid}`;
  const maskId = `award-mask-${uid}`;
  const keyframes = OVERLAY_COLORS.map((_, i) => {
    const base = i * 10;
    const peak = (i + 1) * 10;
    return `@keyframes award-overlay-${uid}-${String(i + 1)} {
      0% { transform: rotate(${String(base)}deg); }
      50% { transform: rotate(${String(peak)}deg); }
      100% { transform: rotate(${String(base)}deg); }
    }`;
  }).join(' ');

  const fill = BACKGROUND_BY_PLACE[(place ?? 2) - 1] ?? BACKGROUND_BY_PLACE[1];
  const resolvedLabel = label ?? `${TITLE_BY_TYPE[type]}${place ? ` #${String(place)}` : ''}`;

  return (
    <div
      role="img"
      aria-label={resolvedLabel}
      className={className}
      style={{ width: '100%', maxWidth: 260 }}
    >
      <style>{keyframes}</style>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 260 54" className="h-auto w-full">
        <defs>
          <filter id={blurId}>
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" />
          </filter>
          <mask id={maskId}>
            <rect width="260" height="54" fill="white" rx="10" />
          </mask>
        </defs>
        <rect width="260" height="54" rx="10" fill={fill} />
        <rect
          x="4"
          y="4"
          width="252"
          height="46"
          rx="8"
          fill="transparent"
          stroke="#bbb"
          strokeWidth="1"
        />
        <text
          fontFamily="Helvetica-Bold, Helvetica"
          fontSize="20"
          fontWeight="bold"
          fill="#444"
          textAnchor="middle"
          x="130"
          y="35"
        >
          {resolvedLabel}
        </text>
        <g transform="translate(8, 9)">
          <path
            fill="#666"
            d="M14.963 9.075c.787-3-.188-5.887-.188-5.887S12.488 5.175 11.7 8.175c-.787 3 .188 5.887.188 5.887s2.25-1.987 3.075-4.987m-4.5 1.987c.787 3-.188 5.888-.188 5.888S7.988 14.962 7.2 11.962c-.787-3 .188-5.887.188-5.887s2.287 1.987 3.075 4.987m.862 10.388s-.6-2.962-2.775-5.175C6.337 14.1 3.375 13.5 3.375 13.5s.6 2.962 2.775 5.175c2.213 2.175 5.175 2.775 5.175 2.775m3.3 3.413s-1.988-2.288-4.988-3.075-5.887.187-5.887.187 1.987 2.287 4.988 3.075c3 .787 5.887-.188 5.887-.188Zm6.75 0s1.988-2.288 4.988-3.075c3-.826 5.887.187 5.887.187s-1.988 2.287-4.988 3.075c-3 .787-5.887-.188-5.887-.188ZM32.625 13.5s-2.963.6-5.175 2.775c-2.213 2.213-2.775 5.175-2.775 5.175s2.962-.6 5.175-2.775c2.175-2.213 2.775-5.175 2.775-5.175M28.65 6.075s.975 2.887.188 5.887c-.826 3-3.076 4.988-3.076 4.988s-.974-2.888-.187-5.888c.788-3 3.075-4.987 3.075-4.987m-4.5 7.987s.975-2.887.188-5.887c-.788-3-3.076-4.988-3.076-4.988s-.974 2.888-.187 5.888c.788 3 3.075 4.988 3.075 4.988ZM18 26.1c.975-.225 3.113-.6 5.325 0 3 .788 5.063 3.038 5.063 3.038s-2.888.975-5.888.187a13 13 0 0 1-1.425-.525c.563.788 1.125 1.425 2.288 1.913l-.863 2.062c-2.063-.862-2.925-2.137-3.675-3.262-.262-.375-.525-.713-.787-1.05-.26.293-.465.586-.686.903l-.102.147-.048.068c-.775 1.108-1.643 2.35-3.627 3.194l-.862-2.062c1.162-.488 1.725-1.125 2.287-1.913-.45.225-.938.375-1.425.525-3 .788-5.887-.187-5.887-.187s1.987-2.288 4.987-3.075c2.212-.563 4.35-.188 5.325.037"
          />
        </g>
        <g style={{ mixBlendMode: 'overlay' }} mask={`url(#${maskId})`}>
          {OVERLAY_COLORS.map((color, i) => (
            <g
              key={String(i)}
              style={{
                transformOrigin: 'center center',
                animation: `award-overlay-${uid}-${String(i + 1)} 5s infinite`,
                willChange: 'transform',
              }}
            >
              <polygon
                points="0,0 260,54 260,0 0,54"
                fill={color}
                filter={`url(#${blurId})`}
                opacity="0.5"
              />
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
}
